import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { formatFileSize, getExpiryNumber } from "../../../../Utils/utils";

export default function CircleProgress({
  sites,
  storage,
  plan,
  sitesLimit,
  storageLimit,
  planLimit,
}) {
  const outerRadius = 55; // Outer radius
  const middleRadius = 45; // Middle circle radius (with gap)
  const innerRadius = 25; // Inner circle radius (with gap)

  const outerStrokeWidth = 5; // Width of the outer stroke (Purple)
  const middleStrokeWidth = 15; // Increased width for the blue circle
  const innerStrokeWidth = 15; // Increased width for the yellow circle

  // Outer circle (Purple)
  const outerCircumference = 2 * Math.PI * (outerRadius - outerStrokeWidth / 2);

  // Middle circle (Blue)
  const middleCircumference =
    2 * Math.PI * (middleRadius - middleStrokeWidth / 2);

  // Inner circle (Yellow)
  const innerCircumference = 2 * Math.PI * (innerRadius - innerStrokeWidth / 2);

  // Initial state for progress values
  const [progressOuter, setProgressOuter] = useState(0);
  const [progressMiddle, setProgressMiddle] = useState(0);
  const [progressInner, setProgressInner] = useState(0);
  const plandaysLeft = getExpiryNumber(planLimit);
  const totalDays = Math.floor((planLimit - plan) / (1000 * 60 * 60 * 24));

  // days left
  const daysLeft = Math.max(
    0,
    Math.floor((planLimit - Date.now()) / (1000 * 60 * 60 * 24))
  );

  // days elapsed
  const daysElapsed = totalDays - daysLeft;

  // % left for outer circle
  const percentageElapsed = Number(
    ((daysElapsed / totalDays) * 100).toFixed(2)
  );

  useEffect(() => {
    setTimeout(() => {
      // Calculate percentage progress based on limits
      setProgressOuter(percentageElapsed);
      setProgressMiddle((storage / (storageLimit * 1024 * 1024 * 1024)) * 100);
      setProgressInner((sites / sitesLimit) * 100);
    }, 100);
  }, [sites, storage, plan, sitesLimit, storageLimit, planLimit]);

  // Calculate stroke dash offsets for each circle
  const dashOffsetOuter =
    outerCircumference - (progressOuter / 100) * outerCircumference;

  const dashOffsetMiddle =
    middleCircumference - (progressMiddle / 100) * middleCircumference;

  const dashOffsetInner =
    innerCircumference - (progressInner / 100) * innerCircumference;

  const cx = outerRadius;
  const cy = outerRadius;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        width: outerRadius * 2,
        height: outerRadius * 2,
      }}
    >
      {/* Plan Expire Circle */}
      <Tooltip title={`${plandaysLeft} days left`}>
        <svg
          width={outerRadius * 2}
          height={outerRadius * 2}
          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", cursor: "pointer" }}
        >
          <circle
            stroke="#f3f3f3"
            fill="none"
            cx={cx}
            cy={cy}
            r={outerRadius - outerStrokeWidth / 2}
            strokeWidth={outerStrokeWidth}
          />
          <circle
            stroke="#A9BAD1"
            fill="none"
            cx={cx}
            cy={cy}
            r={outerRadius - outerStrokeWidth / 2}
            strokeWidth={outerStrokeWidth}
            strokeDasharray={outerCircumference}
            strokeDashoffset={dashOffsetOuter}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        </svg>
      </Tooltip>

      {/* Storage Circle */}
      <Tooltip title={`${formatFileSize(storage)} out of ${storageLimit} GB`}>
        <svg
          width={outerRadius * 2}
          height={outerRadius * 2}
          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", cursor: "pointer" }}
        >
          <circle
            stroke="#f3f3f3"
            fill="none"
            cx={cx}
            cy={cy}
            r={middleRadius - middleStrokeWidth / 2}
            strokeWidth={middleStrokeWidth}
          />
          <circle
            stroke="#D2DFF1"
            fill="none"
            cx={cx}
            cy={cy}
            r={middleRadius - middleStrokeWidth / 2}
            strokeWidth={middleStrokeWidth}
            strokeDasharray={middleCircumference}
            strokeDashoffset={dashOffsetMiddle}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        </svg>
      </Tooltip>

      {/* Sites Circle */}
      <Tooltip title={`${sites} out of ${sitesLimit} sites`}>
        <svg
          width={outerRadius * 2}
          height={outerRadius * 2}
          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", cursor: "pointer" }}
        >
          <circle
            stroke="#f3f3f3"
            fill="none"
            cx={cx}
            cy={cy}
            r={innerRadius - innerStrokeWidth / 2}
            strokeWidth={innerStrokeWidth}
          />
          <circle
            stroke="#135FC2"
            fill="none"
            cx={cx}
            cy={cy}
            r={innerRadius - innerStrokeWidth / 2}
            strokeWidth={innerStrokeWidth}
            strokeDasharray={innerCircumference}
            strokeDashoffset={dashOffsetInner}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        </svg>
      </Tooltip>
    </div>
  );
}